import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { ReactComponent as Logo } from "../../assets/images/logo.svg";
import { ReactComponent as LinkIcon } from "../../../src/assets/icons/links.svg";
import { ReactComponent as EyeOpen } from "../../../src/assets/icons/eye-open.svg";
import { ReactComponent as EyeClose } from "../../../src/assets/icons/eye-close.svg";
import { ReactComponent as Spinner } from "../../assets/icons/spinner.svg";
import { useDispatch, useSelector } from "react-redux";
import { useGetUserQuery, useLoginMutation } from "../../store/auth/usersApiSlice";
import { clearUserInfo, hideIdleModal, hideLoginModal, setExpireTime, setToken, setUserInfo } from "../../store/auth/authSlice";
import { CommonToast } from "../../components/common/Toast";
import { validateEmail } from "../../utils/Helper";
import ReactGA from "react-ga4";
import { useGetMagicTokenMutation } from "../../store/Company/companyApiSlice";
import { plansInformation } from "../../constants/constants";
import SessionConflictModal from "../../components/common/SessionConflictModal";

export default function Login({ modalLogin = false, preventRoute = false }) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({});
  const [showSessionPopup, setShowSessionPopup] = useState(false);
  const {
    token,
  } = useSelector((state) => state.auth);
  const location = useLocation();
  const redirectStep = location?.state?.redirectToStep ?? null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isArticle = params.get('app') === "article";
  const articleSlug = params.get('slug');
  const inviteCodeParams = params.get('invite_code');
  const [inviteCode, setInviteCode] = useState(localStorage.getItem('invite_code'));
  const promptPage = location.pathname.includes("prompt");
  const dashboardPage = location.pathname.includes("dashboard");
  const lastVisited = localStorage.getItem('lastVisited') ?? "";
  const [error, setError] = useState("");
  const [login, { isLoading }] = useLoginMutation();
  const [userToken, setUserToken] = useState("");
  const [magicToken, setMagicToken] = useState("");
  const [getMagicToken, { isLoading: isMagicLoading }] = useGetMagicTokenMutation()
  const { userInfo } = useSelector((state) => state.auth);
  const { data, refetch, isSuccess, isError } = useGetUserQuery(userToken, {
    skip: !userToken,
    refetchOnMount: true,
  });
  const [getSearchParams] = useSearchParams();

  const articleRedirect = (token) => {
    const baseUrl = `${process.env.REACT_APP_K_HUB_URL}/magic/${token}`;
    // Only append slug if it's available
    const url = articleSlug ? `${baseUrl}?slug=${articleSlug}` : baseUrl;
    window.open(url, "_blank");

  }
  useEffect(() => {
    if (inviteCodeParams) {
      setInviteCode(inviteCodeParams);
    }
  }, [inviteCodeParams])

  useEffect(() => {
    if (userInfo) {
      if (userInfo?.is_first_time_setted_password) {
        navigate(`/reset-password?token=${userToken}&email=${userInfo?.email}`);
      }
      else {
        if (!isArticle && !preventRoute) {
          // window.open(`${process.env.REACT_APP_K_HUB_URL}/magic/${magicToken}`, `_blank`)
          navigate(promptPage ? `/gpt-prompts/access` : `/`);
        }
      }
    }
  }, [navigate, userInfo]);

  useEffect(() => {
    const redirect = async () => {

      if (isArticle) {
        const res = await getMagicToken({ token });
        if (res?.data?.token) {
          articleRedirect(res?.data?.token);
          navigate(dashboardPage ? `/dashboard` : `/`);
        } else {
          dispatch(clearUserInfo());
        }
      }
    };
    redirect();
  }, [isArticle, token])

  useEffect(() => {
    if (isSuccess) {
      dispatch(setUserInfo({ ...data }));
    }
  }, [data, dispatch, isSuccess]);

  function togglePasswordVisibility() {
    setIsPasswordVisible((prevState) => !prevState);
  }

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleLogin = async (e, forced = false) => {
    e.preventDefault();
    try {
      if (!validateEmail(formData.email)) {
        setError("Invalid Email, Please enter a valid email")
      } else {
        setError("");
        const splittedData = lastVisited ? lastVisited?.split("/") : [];
        const res = await login({
          email: formData.email.toLowerCase(),
          password: formData.password,
          ...(forced && { force_login: true }),
          ...(isArticle && { return_magic_token: true }),
          ...(inviteCode && { invite_code: inviteCode }),
          ...(splittedData?.[1] === "claim" && { validate_id: splittedData[2] }),
          ...(modalLogin && { referance: splittedData[2] }),
        }).unwrap();

        if (res?.status) {
          ReactGA.event({
            category: 'Login',
            action: 'Login Flow:Attempted to Login; Login Failed',
            label: 'Attempted to Login; Login Failed',
          });
          if (res?.status === "LOGIN_FAILED") {
            setError(res?.message ? res?.message : "Validation Failed")
          } else if (res?.status === "ALREADY_LOGGED_IN") {
            setShowSessionPopup(true);
            return;
          }
        }
        if (res?.reset_token) {
          ReactGA.event({
            category: 'Login',
            action: 'Login Flow:Asked user to reset your password',
            label: 'Asked user to reset your password',
          });
          navigate(`/reset-password?token=${res?.reset_token}&email=${formData.email.toLowerCase()}`)
        } else {
          let token = res?.token || res?.access
          dispatch(setExpireTime(res?.expire_time))
          setUserToken(token);
          dispatch(setToken(token));
          if (token) {
            ReactGA.event({
              category: 'Login',
              action: 'Login Flow:Attempted to Login; Login Success',
              label: 'Attempted to Login; Login Success',
            });
            if (isSuccess) {
              dispatch(setUserInfo({ ...data }));
              setShowSessionPopup(false);
              dispatch(hideLoginModal());
              dispatch(hideIdleModal());
            }
            const slug = localStorage.getItem('slug');
            if (redirectStep) {
              navigate("/sign-up", {
                state: { redirectToStep: 6 },
              })
            } else if (slug && lastVisited == "/plans") {
              let plan = plansInformation.filter((item) => item.slug === slug);
              const companyDataString = localStorage.getItem("payasyougoCompanyID");
              window.scrollTo(0, 0);
              navigate("/plans/paymentdetails", {
                state: {
                  planDetails: plan[0],
                  company_id: JSON.parse(JSON.stringify(companyDataString)),
                },
              });
            }
            else if (lastVisited) {
              if (res?.validate_id === true) {
                navigate(`/search/${splittedData[2]}`)
              } else {
                navigate(`${lastVisited}`);
              }
            } else {
              if (isArticle) {
                articleRedirect(res.magic_token)
              }
              if (preventRoute) {
                dispatch(hideLoginModal());
              } else {
                navigate(dashboardPage ? `/dashboard` : `/`);
              }
            }
          } else {
            CommonToast.error("Token is required");
            const url = isArticle && articleSlug ? `/login?app=article&slug=${articleSlug}` : "/login?app=article"
            navigate(dashboardPage ? "/login/dashboard" : isArticle ? url : "/login");
          }
        }
      }
    } catch (error) {
      setError(
        error?.data
          ? Object.values(error?.data)[0]
          : "An error occurred. Please try again later."
      );
    }
  };

  // useEffect(() => {
  //   if (data) {
  //     refetch();
  //   }
  // }, [refetch, data, userToken]);

  const handleForgotPassword = () => {
    ReactGA.event({
      category: 'Fogot Password',
      action: 'Login Flow:Clicked on forgot password button',
      label: 'Clicked on forgot password button',
    });
  }

  const handleCreateNewAccount = () => {
    ReactGA.event({
      category: 'Create New Account',
      action: 'Login Flow:Clicked on Create New Account button',
      label: 'Clicked on Create New Account button',
    });
  }

  return (
    <>
      {showSessionPopup && <SessionConflictModal setShowSessionPopup={setShowSessionPopup} handleForceLogin={(e) => handleLogin(e, true)} />}
      <div
        id="login"
        className={`${modalLogin ? "py-0" : "py-12"}flex h-full flex-1 flex-col justify-between px-4`}
      >
        <div className={`${modalLogin ? "mt-0" : "mt-10"} md:mx-auto md:w-full md:max-w-xl sm:max-w-sm sm:w-full sm:mx-auto`}>
          {!modalLogin && <div className="md:mx-auto">
            <Link to="/">
              <div className="flex justify-center items-center md:mx-auto" style={{ margin: '10px' }}>
                <Logo />
              </div>
            </Link>
            <p className="text-light text-para3 mt-2">
              Don't have an account? {" "}
              <Link to="/sign-up" onClick={handleCreateNewAccount} className="font-bold">
                Create account
              </Link>
            </p>
          </div>}
          <form className={`${modalLogin ? "space-y-4" : "space-y-6 mt-12"}`} onSubmit={handleLogin}>
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium leading-6 text-[#3C4848] text-left ${modalLogin ? "text-[12px]" : "text-[16px]"}`}
              >
                Email
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email address"
                  onChange={handleChange}
                  required
                  // style={modalLogin ? { border: "none", backgroundColor: "#FAFAFA" } : {}}
                  className={`block w-full py-3 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 placeholder:text-[12px] sm:text-sm sm:leading-6 ${modalLogin ? "rounded-md" : "rounded-md"}`}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className={`block text-sm font-medium leading-6 text-[#3C4848] ${modalLogin ? "text-[12px]" : "text-[16px]"}`}
                >
                  Password
                </label>
              </div>
              <div className="mt-2 relative">
                <input
                  id="password"
                  name="password"
                  type={isPasswordVisible ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  required
                  onChange={handleChange}
                  // style={modalLogin ? { border: "none", backgroundColor: "#FAFAFA" } : {}}
                  className={`block w-full py-3 px-3 text-gray-900 shadow-sm  placeholder:text-gray-400 placeholder:text-[12px] sm:text-sm sm:leading-6 ${modalLogin ? "rounded-md" : "rounded-md"}`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  {isPasswordVisible ? (
                    <EyeOpen onClick={togglePasswordVisibility} />
                  ) : (
                    <EyeClose onClick={togglePasswordVisibility} />
                  )}
                </div>
              </div>
              {!modalLogin && <div className="text-sm text-left py-2">
                <Link
                  to="/reset-password"
                  onClick={handleForgotPassword}
                  className="text-left text-para3 font-bold"
                >
                  Forgot Your password?
                </Link>
              </div>}
            </div>
            {error && <div className="text-left text-red-500">{error}</div>}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`flex w-full justify-center rounded-md px-3 py-3 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${isLoading ? "cursor-not-allowed bg-disable" : "cursor-pointer bg-primary"}`} style={{ marginBottom: '15px' }}
              >
                {isLoading ? <Spinner /> : "Log in"}
              </button>
            </div>
          </form>
          {getSearchParams.get("guest") === "true" ? (
            <div className="mt-5 mb-5">
            </div>
          ) : null}
        </div>

        {!modalLogin ? <div>
          <p>{`Copyright @ ${new Date().getFullYear()} TREVEX, Inc`} </p>
          <Link
            to="/privacy-policy"
            className="inline-flex items-center justify-center mr-2 gap-2 font-bold"
          >
            Privacy Policy <LinkIcon />
          </Link>
        </div> : <p className="text-[#787878] text-[12px] mt-2">
          Don't have an account? {" "}
          <Link className="text-[#28475C] font-bold" to="/sign-up" onClick={handleCreateNewAccount}>
            Register now
          </Link>
        </p>}
      </div>
    </>
  );
}
